import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import SiriWave from 'siriwave';

// Idle "breathing" baseline — kept subtle so silence looks calm
const IDLE_AMPLITUDE = 0.15;
const IDLE_SPEED = 0.02;

// Active voice range
const MAX_AMPLITUDE = 8;
const MAX_SPEED = 0.12;

// Smoothing factor — lower = smoother (exponential moving average)
const SMOOTHING = 0.4;

// Noise gate — ignore ambient mic noise below this threshold (0–1 scale)
const NOISE_GATE = 0.03;

const WAVEFORM_HEIGHT = 200;

interface VoiceWaveformProps {
  analyserRef: React.RefObject<AnalyserNode | null>;
  active: boolean;
  paused?: boolean;
  previewing?: boolean;
}

const VoiceWaveform = ({ analyserRef, active, paused = false, previewing = false }: VoiceWaveformProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveRef = useRef<SiriWave | null>(null);
  const rafRef = useRef<number | null>(null);
  const smoothedRef = useRef(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const wave = new SiriWave({
      container,
      style: 'ios9',
      width: container.offsetWidth,
      height: WAVEFORM_HEIGHT,
      speed: IDLE_SPEED,
      amplitude: IDLE_AMPLITUDE,
      autostart: true,
      cover: true,
      lerpSpeed: 0.05,
    });

    waveRef.current = wave;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && wave.canvas) {
        const newWidth = entry.contentRect.width;
        const ratio = wave.opt.ratio ?? window.devicePixelRatio;
        wave.canvas.width = newWidth * ratio;
        wave.canvas.style.width = `${newWidth}px`;
        wave.width = newWidth * ratio;
      }
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      wave.dispose();
      waveRef.current = null;
      rafRef.current = null;
      smoothedRef.current = 0;
    };
  }, []);

  // Drive amplitude + speed from mic input (active recording, preview, or idle)
  useEffect(() => {
    const wave = waveRef.current;
    if (!wave) return;

    const shouldDrive = (active && !paused) || previewing;

    if (!shouldDrive) {
      wave.setAmplitude(IDLE_AMPLITUDE);
      wave.setSpeed(IDLE_SPEED);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const dataArray = new Uint8Array(256);

    const tick = () => {
      const analyser = analyserRef.current;
      if (analyser) {
        analyser.getByteFrequencyData(dataArray);

        // Compute average level from frequency data (0–1)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const raw = sum / dataArray.length / 255;

        // Noise gate — suppress ambient mic noise so silence looks calm
        const gated = raw < NOISE_GATE ? 0 : raw;

        // Power curve to exaggerate voice peaks — makes quiet->loud more dramatic
        const boosted = Math.pow(gated, 0.45);

        // Smooth to avoid jitter
        smoothedRef.current += (boosted - smoothedRef.current) * SMOOTHING;
        const level = smoothedRef.current;

        // Map to siriwave params
        const amplitude = IDLE_AMPLITUDE + level * (MAX_AMPLITUDE - IDLE_AMPLITUDE);
        const speed = IDLE_SPEED + level * (MAX_SPEED - IDLE_SPEED);

        wave.setAmplitude(amplitude);
        wave.setSpeed(speed);
      } else {
        // Analyser not ready yet (mic still initializing) — keep breathing
        wave.setAmplitude(IDLE_AMPLITUDE);
        wave.setSpeed(IDLE_SPEED);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [active, paused, previewing, analyserRef]);

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: WAVEFORM_HEIGHT,
          '& canvas': {
            display: 'block',
          },
        }}
      />
    </Box>
  );
};

export default VoiceWaveform;
